import { useEffect, useMemo } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, MapPin, Lightbulb, AlertTriangle, ChevronRight } from "lucide-react";
import { getGuideBySlug, GUIDES } from "@/data/guides";
import { LiveMapButton } from "@/components/LiveMapButton";

const Callout = ({ callout }) => {
  const warning = callout.type === "warning";
  const Icon = warning ? AlertTriangle : Lightbulb;
  const color = warning ? "#ffaa00" : "#2ec4b6";
  return (
    <div data-testid="guide-callout" className="flex gap-3 rounded-2xl border p-5 my-6" style={{ borderColor: `${color}55`, background: `${color}10` }}>
      <Icon size={18} style={{ color }} className="shrink-0 mt-0.5" />
      <p className="text-sm leading-relaxed text-zinc-100">
        <span className="font-heading font-bold uppercase tracking-wider text-xs mr-2" style={{ color }}>{warning ? "Watch out" : "Pro tip"}</span>
        {callout.text}
      </p>
    </div>
  );
};

const SectionTable = ({ table }) => (
  <div className="mt-5 overflow-x-auto rounded-2xl border border-white/10">
    <table className="w-full text-sm">
      <thead>
        <tr className="bg-white/[0.06]">
          {table.columns.map((c) => (
            <th key={c} className="text-left font-heading font-semibold text-white px-4 py-3">{c}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row) => (
          <tr key={row.join("|")} className="border-t border-white/10">
            {row.map((cell, j) => (
              <td key={`${table.columns[j]}-${cell.slice(0, 16)}`} className="px-4 py-3 text-zinc-300 leading-relaxed">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const TableOfContents = ({ sections }) => (
  <nav data-testid="guide-toc" className="mt-8 bg-white/[0.03] border border-white/10 rounded-2xl p-6">
    <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-3">In this guide</p>
    <ol className="space-y-1.5 columns-1 sm:columns-2 gap-8">
      {sections.map((s, i) => (
        <li key={s.heading} className="break-inside-avoid">
          <a
            href={`#${slugify(s.heading)}`}
            data-testid="guide-toc-link"
            className="text-sm text-zinc-300 hover:text-[#2ec4b6] transition-colors leading-snug inline-block"
          >
            <span className="text-zinc-500 font-mono text-xs mr-2">{String(i + 1).padStart(2, "0")}</span>
            {s.heading}
          </a>
        </li>
      ))}
      <li>
        <a href="#faqs" data-testid="guide-toc-link" className="text-sm text-zinc-300 hover:text-[#2ec4b6] transition-colors leading-snug inline-block">
          <span className="text-zinc-500 font-mono text-xs mr-2">{String(sections.length + 1).padStart(2, "0")}</span>
          Frequently Asked Questions
        </a>
      </li>
    </ol>
  </nav>
);

const Section = ({ section }) => (
  <section id={slugify(section.heading)} className="mt-10 scroll-mt-24">
    <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-4">{section.heading}</h2>
    {section.paragraphs?.map((p) => (
      <p key={p.slice(0, 40)} className="text-base text-zinc-300 leading-relaxed mt-4">{p}</p>
    ))}
    {section.list && (
      <ul className="mt-5 space-y-3">
        {section.list.map((li) => (
          <li key={li} className="flex gap-3 text-sm text-zinc-200 leading-relaxed bg-white/[0.03] border border-white/10 rounded-xl px-4 py-3">
            <span className="w-1.5 h-1.5 rounded-full bg-[#2ec4b6] shrink-0 mt-2" />
            {li}
          </li>
        ))}
      </ul>
    )}
    {section.table && <SectionTable table={section.table} />}
    {section.paragraphs2?.map((p) => (
      <p key={p.slice(0, 40)} className="text-zinc-300 leading-relaxed mt-4">{p}</p>
    ))}
    {section.subsections?.map((sub) => (
      <div key={sub.heading} className="mt-6">
        <h3 className="font-heading text-xl font-semibold text-white mb-2">{sub.heading}</h3>
        {sub.paragraphs.map((p) => (
          <p key={p.slice(0, 40)} className="text-base text-zinc-300 leading-relaxed mt-3">{p}</p>
        ))}
      </div>
    ))}
    {section.callout && <Callout callout={section.callout} />}
  </section>
);

export default function GuidePage() {
  const { slug } = useParams();
  const guide = getGuideBySlug(slug);
  const relatedGuides = useMemo(() => GUIDES.filter((g) => g.slug !== slug), [slug]);

  useEffect(() => {
    if (!guide) return;
    document.title = guide.seoTitle || `${guide.title} | wherecaniflymydrone`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    const prevDesc = meta.content;
    if (guide.metaDescription) meta.content = guide.metaDescription;
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: guide.title,
        description: guide.metaDescription || guide.description,
        dateModified: guide.updated,
        author: { "@type": "Organization", name: "wherecaniflymydrone.co.uk" },
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: guide.faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
      },
    ]);
    document.head.appendChild(script);
    window.scrollTo(0, 0);
    return () => {
      document.head.removeChild(script);
      meta.content = prevDesc;
    };
  }, [guide]);

  if (!guide) return <Navigate to="/guides" replace />;

  return (
    <div data-testid="guide-page" className="min-h-screen bg-[#0a0a0a] text-zinc-100">
      <div className="relative h-[46vh] min-h-[340px] overflow-hidden">
        <img src={guide.hero} alt={guide.title} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-[#0a0a0a]/60 to-[#0a0a0a]/20" />
        <div className="relative z-10 max-w-3xl mx-auto px-6 h-full flex flex-col justify-end pb-10">
          <div className="flex items-center justify-between mb-4">
            <Link to="/guides" data-testid="guide-back-link" className="inline-flex items-center gap-1.5 text-xs text-zinc-300 hover:text-[#2ec4b6] transition-colors w-fit">
              <ArrowLeft size={14} /> All guides
            </Link>
            <LiveMapButton testId="guide-header-open-map" size="sm" />
          </div>
          <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#2ec4b6] mb-3">{guide.category}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight">{guide.title}</h1>
          <div className="flex items-center gap-4 mt-4 text-xs text-zinc-300">
            <span className="inline-flex items-center gap-1"><Clock size={12} /> {guide.readTime} min read</span>
            <span>Updated {guide.updated}</span>
          </div>
        </div>
      </div>

      <article className="max-w-3xl mx-auto px-6 py-12">
        <p className="text-lg text-zinc-200 leading-relaxed border-l-2 border-[#2ec4b6] pl-5">{guide.intro}</p>
        <TableOfContents sections={guide.sections} />
        {guide.sections.map((s) => (
          <Section key={s.heading} section={s} />
        ))}

        <section id="faqs" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl sm:text-3xl font-semibold tracking-tight mb-6">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {guide.faqs.map((f) => (
              <div key={f.q} data-testid="guide-faq" className="bg-white/[0.03] border border-white/10 rounded-2xl p-5">
                <h3 className="font-heading font-semibold text-base text-white">{f.q}</h3>
                <p className="text-sm text-zinc-300 mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </section>

        <div className="mt-12 rounded-3xl border border-[#2ec4b6]/40 bg-[#2ec4b6]/10 p-7 flex flex-col sm:flex-row items-start sm:items-center gap-5">
          <div className="flex-1">
            <p className="font-heading font-bold text-lg text-white">Check your exact spot before you fly</p>
            <p className="text-sm text-zinc-300 mt-1">Tap the live map or ask Maverick, our CAP 722 AI, for an instant clearance report.</p>
          </div>
          <Link to="/map" data-testid="guide-map-cta" className="live-map-btn inline-flex items-center gap-2 bg-[#2ec4b6] text-black font-heading font-semibold text-sm px-6 py-3 rounded-full hover:bg-[#3dd8ca] transition-colors shrink-0">
            <MapPin size={15} /> Open the live map <ChevronRight size={15} />
          </Link>
        </div>

        <section className="border-t border-white/10 pt-10 mt-12">
          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-400 mb-4">More guides</p>
          <div className="flex flex-wrap gap-2">
            {relatedGuides.map((g) => (
              <Link key={g.slug} to={`/guides/${g.slug}`} data-testid="related-guide-link" className="text-xs px-4 py-2 rounded-full border border-white/15 text-zinc-200 hover:border-[#2ec4b6]/50 hover:text-[#2ec4b6] transition-colors">
                {g.title}
              </Link>
            ))}
          </div>
        </section>
      </article>
    </div>
  );
}
